import type { PosPriceMap, PosProduct, PosVariant } from './api';

/**
 * Shared POS price derivation, used by the product grid, the cart, the item
 * sheet and the checkout page. These live in one module so the numbers on a
 * tile and the numbers on the matching cart line always agree. The matching
 * display component is `components/pos/price-tag.tsx`.
 */

/**
 * Live cash price of a product/variant = the selected slot's discounted price
 * (from the price-map overlay) when present, else the branch base price. `base`
 * is the SRP shown struck-through when a discount applies. Deriving price at
 * render (not storing it on the cart line) is what makes switching slots
 * re-price the grid AND already-carted lines instantly, with no stale price.
 */
export function productPrice(
  p: PosProduct,
  priceMap: PosPriceMap | null,
): { now: number; base: number } {
  return {
    now: Number(priceMap?.products[p.id]?.unitPrice ?? p.effectivePrice),
    base: Number(p.basePrice),
  };
}

export function variantPrice(
  v: PosVariant,
  priceMap: PosPriceMap | null,
): { now: number; base: number } {
  return { now: Number(priceMap?.variants[v.id]?.unitPrice ?? v.price), base: Number(v.price) };
}

/** A product+variant pair, i.e. the shape of a cart line for pricing purposes. */
export type PricedLine = { product: PosProduct; variant: PosVariant | null };

/** Unit price for a cart line, slot-discounted when the price map has it. */
export function lineUnitPrice(l: PricedLine, priceMap: PosPriceMap | null): number {
  return l.variant ? variantPrice(l.variant, priceMap).now : productPrice(l.product, priceMap).now;
}

/** Stock cap for a line: the chosen variant's branch stock, else the product's. */
export function lineStockCap(l: PricedLine): number {
  return l.variant ? l.variant.stockQty : l.product.sellableQty;
}
